import React from "react";
import {
  Box,
  Container,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { isSuperAdmin } from "../../utils/roles";
import BillingAccessBanner from "./BillingAccessBanner";
import DashboardTopAreaNavigation from "./DashboardTopAreaNavigation";
import DashboardTopGlobalControls from "./DashboardTopGlobalControls";
import DashboardSecondaryNavigation from "./DashboardSecondaryNavigation";
import MobileDashboardShell from "./mobile/MobileDashboardShell";
import {
  rememberDashboardNavigationContext,
  resolveDashboardNavigation,
  resolveDashboardRouteContext,
} from "../../utils/dashboardNavigation";
import { WEBPROFIT_RESPONSIVE, dashboardContentContainerSx, dashboardFormScopeSx } from "../../theme/webProfitResponsive";

const BRAND = {
  dark: "#19242F",
  bg: "#F6F7FB",
};

function projectMatchesKey(project, key) {
  if (!project || !key) return false;
  return [project.slug, project.id, project._id]
    .filter(Boolean)
    .some((value) => String(value) === String(key));
}

export default function DashboardShell() {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const { user } = useAuth();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const superAdmin = isSuperAdmin(user);
  const routeContext = resolveDashboardRouteContext(location.pathname);
  const routeProjectKey = routeContext.projectKey;
  const routeProject = React.useMemo(
    () => projects.find((project) => projectMatchesKey(project, routeProjectKey)) || null,
    [projects, routeProjectKey],
  );
  const displayedProject = routeProjectKey
    ? (routeProject || (projectMatchesKey(activeProject, routeProjectKey) ? activeProject : null))
    : activeProject;
  const topNavigation = React.useMemo(
    () => resolveDashboardNavigation({
      project: displayedProject,
      superAdmin,
      pathname: location.pathname,
      search: location.search,
    }),
    [displayedProject, superAdmin, location.pathname, location.search],
  );

  React.useEffect(() => {
    rememberDashboardNavigationContext(topNavigation, {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [topNavigation, location.pathname, location.search, location.hash]);

  React.useEffect(() => {
    if (!routeProjectKey) return;
    if (!projects.length) {
      loadProjects();
      return;
    }
    if (routeProject && !projectMatchesKey(activeProject, routeProjectKey)) {
      setActiveProject(routeProject);
    }
  }, [routeProjectKey, routeProject, projects.length, activeProject, setActiveProject, loadProjects]);

  if (!isLgUp) return <MobileDashboardShell projectOverride={displayedProject} />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BRAND.bg }}>
      <Box component="main" sx={{ minWidth: 0, ...dashboardFormScopeSx }}>
        <Box
          component="header"
          sx={{
            minHeight: 68,
            pl: `calc(20px + ${WEBPROFIT_RESPONSIVE.safeLeft})`,
            pr: `calc(20px + ${WEBPROFIT_RESPONSIVE.safeRight})`,
            display: "grid",
            gridTemplateColumns: "minmax(132px, 1fr) minmax(0, auto) minmax(230px, 1fr)",
            gap: 1.5,
            alignItems: "center",
            bgcolor: "rgba(255,255,255,.94)",
            borderBottom: "1px solid rgba(25,36,47,.08)",
            position: "sticky",
            top: 0,
            zIndex: 1100,
            backdropFilter: "blur(12px)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.1 }}>Panel</Typography>
            <Typography variant="caption" noWrap sx={{ display: "block", maxWidth: 210, color: "rgba(25,36,47,.52)", fontWeight: 750 }}>
              {routeContext.isProject
                ? (displayedProject?.name || "Proyecto")
                : "Cuenta y accesos globales"}
            </Typography>
          </Box>

          <DashboardTopAreaNavigation
            navigation={topNavigation}
            project={displayedProject}
            superAdmin={superAdmin}
          />

          <Box sx={{ justifySelf: "end", minWidth: 0 }}>
            <DashboardTopGlobalControls
              navigation={topNavigation}
              project={displayedProject}
              projects={projects}
              setActiveProject={setActiveProject}
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: "sticky",
            top: 68,
            zIndex: 1090,
            backdropFilter: "blur(12px)",
          }}
        >
          <DashboardSecondaryNavigation
            navigation={topNavigation}
            project={displayedProject}
            superAdmin={superAdmin}
          />
        </Box>
        <Box sx={{ py: 4 }}>
          <Container maxWidth={false} disableGutters sx={dashboardContentContainerSx}>
            {routeContext.isProject ? <BillingAccessBanner project={displayedProject} /> : null}
            <Outlet />
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
